import type { UIMessage } from "@ai-sdk/react";

export type NormalizedChatStreamEvent =
  | { type: "session"; sessionId: string }
  | { type: "text_delta"; text: string }
  | { type: "thinking"; text?: string }
  | { type: "tool_use"; id: string; name: string; input?: unknown }
  | { type: "tool_result"; id: string; output?: unknown; isError?: boolean }
  | { type: "custom_tool_use"; id: string; name: string; input: Record<string, unknown> }
  | { type: "requires_action"; eventIds: string[] }
  | { type: "status_rescheduled" }
  | {
      type: "session_error";
      errorType: string;
      message: string;
      retryStatus: string;
    }
  | { type: "session_terminated" }
  | {
      type: "scout_phase";
      phase:
        | "query_generation"
        | "collection"
        | "distillation"
        | "ad_generation"
        | "ugc_brief";
      status: "running" | "complete" | "failed";
      progress?: { completed: number; total: number; track?: string };
      data?: unknown;
      error?: string;
    }
  | {
      type: "image_progress";
      toolUseId: string;
      sequence: number;
      kind: "thought" | "draft" | "final";
      text?: string;
      imageUrl?: string;
      mimeType?: string;
    }
  | { type: "done" }
  | { type: "error"; message: string };

export interface ManagedToolResultEvent {
  id: string;
  name?: string;
  output?: unknown;
  isError?: boolean;
}

export interface TextPart {
  type: "text";
  text: string;
}

export interface ThinkingPart {
  type: "thinking";
  thinking: string;
}

export interface ReasoningPart {
  type: "reasoning";
  text: string;
}

export interface DynamicToolPart {
  type: "dynamic-tool";
  toolName: string;
  toolCallId: string;
  input: unknown;
  output: unknown;
  state: "input-available" | "output-available" | "output-error";
}

export type NormalizedChatMessagePart =
  | TextPart
  | ThinkingPart
  | ReasoningPart
  | DynamicToolPart;

export interface NormalizedChatStreamCallbacks {
  onImageGenerated?: (imageUrl: string) => void;
  onVideoGenerated?: (videoUrl: string, thumbnailUrl?: string) => void;
  onToolResult?: (event: ManagedToolResultEvent) => void;
  onToolUse?: (event: { id: string; name: string; input: unknown }) => void;
  onScoutPhase?: (event: Extract<NormalizedChatStreamEvent, { type: "scout_phase" }>) => void;
  onImageProgress?: (
    event: Extract<NormalizedChatStreamEvent, { type: "image_progress" }>,
  ) => void;
}

export interface NormalizedChatAccumulatorResult {
  sessionId?: string;
  error?: Error;
  sessionTerminated?: boolean;
  done?: boolean;
  startedStreaming?: boolean;
  rendered?: boolean;
}

interface NormalizedChatAccumulatorOptions {
  messageId?: string;
  createMessageId?: () => string;
  commitMessage: (message: UIMessage, isUpdate: boolean) => void;
  callbacks?: NormalizedChatStreamCallbacks;
  thinkingPartType?: "thinking" | "reasoning";
  flushOnDone?: "always" | "if-media" | "never";
  resetOnDone?: boolean;
}

/**
 * Normalize a URL to an absolute one. Some historical tool outputs stored
 * GCS_PUBLIC_URL without a protocol; UI media renderers require one.
 */
function normalizeUrl(raw: string): string {
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function isRenderableUrl(raw: string): boolean {
  if (!raw || typeof raw !== "string") return false;
  if (/^https?:\/\//i.test(raw)) return true;
  return /^[a-z0-9.-]+\.[a-z]{2,}\/\S+$/i.test(raw);
}

function safeStringify(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    const text = JSON.stringify(value);
    return typeof text === "string" ? text : "";
  } catch {
    return "";
  }
}

function parseJsonIfString(value: unknown): unknown | undefined {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return undefined;
  }
}

function extractImageUrl(output: unknown): string | undefined {
  const parsed = parseJsonIfString(output);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return undefined;
  }
  const url = (parsed as { url?: unknown }).url;
  return typeof url === "string" && isRenderableUrl(url) ? normalizeUrl(url) : undefined;
}

function extractVideoFromOutput(
  output: unknown,
): { videoUrl?: string; thumbnailUrl?: string } {
  if (!output) return {};
  const text = safeStringify(output);
  const parsed = parseJsonIfString(output);

  if (parsed && typeof parsed === "object") {
    const klingVideos = (parsed as { data?: { task_result?: { videos?: unknown } } })
      ?.data?.task_result?.videos;
    if (
      Array.isArray(klingVideos) &&
      typeof (klingVideos[0] as { url?: unknown })?.url === "string"
    ) {
      const klingThumbs = (parsed as { data?: { task_result?: { images?: unknown } } })
        ?.data?.task_result?.images;
      return {
        videoUrl: (klingVideos[0] as { url: string }).url,
        thumbnailUrl:
          Array.isArray(klingThumbs) &&
          typeof (klingThumbs[0] as { url?: unknown })?.url === "string"
            ? (klingThumbs[0] as { url: string }).url
            : undefined,
      };
    }

    let foundVideo: string | undefined;
    let foundThumb: string | undefined;
    const walk = (node: unknown): void => {
      if (foundVideo && foundThumb) return;
      if (!node) return;
      if (Array.isArray(node)) {
        for (const item of node) {
          walk(item);
          if (foundVideo && foundThumb) return;
        }
        return;
      }
      if (typeof node !== "object") return;
      for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
        if (typeof value === "string" && /^https?:\/\//i.test(value)) {
          if (!foundVideo && (key === "videoUrl" || key === "video_url")) {
            foundVideo = value;
          } else if (!foundThumb && (key === "thumbnailUrl" || key === "thumbnail_url")) {
            foundThumb = value;
          }
        } else {
          walk(value);
        }
      }
    };
    walk(parsed);
    if (foundVideo) {
      return { videoUrl: foundVideo, thumbnailUrl: foundThumb };
    }
  }

  const videoMatch = text.match(
    /https?:\/\/[^\s"'<>)]+\.(?:mp4|webm)(?:\?[^\s"'<>)]*)*/i,
  );
  return videoMatch ? { videoUrl: videoMatch[0] } : {};
}

function clonePart(part: NormalizedChatMessagePart): NormalizedChatMessagePart {
  return { ...part };
}

function isTextPart(part: NormalizedChatMessagePart): part is TextPart {
  return part.type === "text";
}

function makeThinkingPart(
  text: string,
  type: "thinking" | "reasoning",
): ThinkingPart | ReasoningPart {
  const label = text || "Extended thinking";
  return type === "reasoning"
    ? { type: "reasoning", text: label }
    : { type: "thinking", thinking: label };
}

function isThinkingLikePart(
  part: NormalizedChatMessagePart | undefined,
): part is ThinkingPart | ReasoningPart {
  return part?.type === "thinking" || part?.type === "reasoning";
}

function appendThinkingText(part: ThinkingPart | ReasoningPart, text: string): void {
  if (!text) return;
  if (part.type === "thinking") {
    part.thinking = part.thinking === "Extended thinking" ? text : `${part.thinking}${text}`;
  } else {
    part.text = part.text === "Extended thinking" ? text : `${part.text}${text}`;
  }
}

export class NormalizedChatMessageAccumulator {
  private messageId: string | null;
  private messageAdded = false;
  private parts: NormalizedChatMessagePart[] = [];
  private currentText: TextPart | null = null;
  private pendingTools = new Map<string, number>();
  private mediaUrls: string[] = [];
  private readonly createMessageId?: () => string;
  private readonly commitMessage: (message: UIMessage, isUpdate: boolean) => void;
  private readonly callbacks: NormalizedChatStreamCallbacks;
  private readonly thinkingPartType: "thinking" | "reasoning";
  private readonly flushOnDone: "always" | "if-media" | "never";
  private readonly resetOnDone: boolean;

  constructor(options: NormalizedChatAccumulatorOptions) {
    this.messageId = options.messageId ?? null;
    this.createMessageId = options.createMessageId;
    this.commitMessage = options.commitMessage;
    this.callbacks = options.callbacks ?? {};
    this.thinkingPartType = options.thinkingPartType ?? "thinking";
    this.flushOnDone = options.flushOnDone ?? "always";
    this.resetOnDone = options.resetOnDone ?? false;
  }

  handleEvent(event: NormalizedChatStreamEvent): NormalizedChatAccumulatorResult {
    switch (event.type) {
      case "session":
        return { sessionId: event.sessionId };
      case "thinking":
        return this.handleThinking(event.text);
      case "text_delta":
        return this.handleTextDelta(event.text);
      case "tool_use":
        return this.handleToolUse(event);
      case "tool_result":
        return this.handleToolResult(event);
      case "scout_phase":
        this.callbacks.onScoutPhase?.(event);
        return {};
      case "image_progress":
        this.callbacks.onImageProgress?.(event);
        return {};
      case "done": {
        const shouldFlush =
          this.flushOnDone === "always" ||
          (this.flushOnDone === "if-media" && this.mediaUrls.length > 0);
        if (shouldFlush) this.flush();
        if (this.resetOnDone) this.reset();
        return { done: true };
      }
      case "error":
        return { error: new Error(event.message) };
      case "session_error":
        return { error: new Error(`${event.errorType}: ${event.message}`) };
      case "session_terminated":
        this.reset();
        return { sessionTerminated: true };
      default:
        return {};
    }
  }

  reset(): void {
    this.messageId = null;
    this.messageAdded = false;
    this.parts = [];
    this.currentText = null;
    this.pendingTools.clear();
    this.mediaUrls = [];
  }

  private ensureMessageId(): string {
    if (!this.messageId) {
      this.messageId = this.createMessageId?.() ?? `normalized-${Date.now()}`;
    }
    return this.messageId;
  }

  private handleThinking(text: string | undefined): NormalizedChatAccumulatorResult {
    this.ensureMessageId();
    this.currentText = null;
    const last = this.parts[this.parts.length - 1];
    const thinkingText = typeof text === "string" ? text : "";
    if (isThinkingLikePart(last) && thinkingText.length > 0) {
      appendThinkingText(last, thinkingText);
      this.flush();
      return { startedStreaming: true, rendered: true };
    }
    if (!isThinkingLikePart(last)) {
      this.parts.push(makeThinkingPart(thinkingText, this.thinkingPartType));
      this.flush();
      return { startedStreaming: true, rendered: true };
    }
    return { startedStreaming: true };
  }

  private handleTextDelta(text: string): NormalizedChatAccumulatorResult {
    this.ensureMessageId();
    const textPart = this.ensureTextPart();
    textPart.text += text;
    this.flush();
    return { startedStreaming: true, rendered: true };
  }

  private handleToolUse(
    event: Extract<NormalizedChatStreamEvent, { type: "tool_use" }>,
  ): NormalizedChatAccumulatorResult {
    this.ensureMessageId();
    this.currentText = null;
    const toolPart: DynamicToolPart = {
      type: "dynamic-tool",
      toolName: event.name,
      toolCallId: event.id,
      input: event.input ?? {},
      output: undefined,
      state: "input-available",
    };
    this.pendingTools.set(event.id, this.parts.length);
    this.parts.push(toolPart);
    this.flush();
    this.callbacks.onToolUse?.({
      id: event.id,
      name: event.name,
      input: event.input ?? {},
    });
    return { startedStreaming: true, rendered: true };
  }

  private handleToolResult(
    event: Extract<NormalizedChatStreamEvent, { type: "tool_result" }>,
  ): NormalizedChatAccumulatorResult {
    const toolIdx = this.pendingTools.get(event.id);
    let toolName: string | undefined;
    if (toolIdx !== undefined && this.parts[toolIdx]?.type === "dynamic-tool") {
      const tool = this.parts[toolIdx] as DynamicToolPart;
      toolName = tool.toolName;
      tool.output = event.output;
      tool.state = event.isError ? "output-error" : "output-available";
      this.pendingTools.delete(event.id);
    }
    this.callbacks.onToolResult?.({
      id: event.id,
      name: toolName,
      output: event.output,
      isError: event.isError,
    });

    if (!event.isError && event.output) {
      const imageUrl = extractImageUrl(event.output);
      if (imageUrl) {
        this.mediaUrls.push(imageUrl);
        this.callbacks.onImageGenerated?.(imageUrl);
      } else {
        const video = extractVideoFromOutput(event.output);
        if (video.videoUrl && video.videoUrl.startsWith("http")) {
          this.mediaUrls.push(video.videoUrl);
          this.callbacks.onVideoGenerated?.(video.videoUrl, video.thumbnailUrl);
        }
      }
    }

    this.flush();
    return { rendered: Boolean(this.messageId) };
  }

  private ensureTextPart(): TextPart {
    if (this.currentText) return this.currentText;
    const textPart: TextPart = { type: "text", text: "" };
    this.parts.push(textPart);
    this.currentText = textPart;
    return textPart;
  }

  private flush(): void {
    if (!this.messageId) return;
    const finalParts = this.parts.map(clonePart);
    if (this.mediaUrls.length > 0) {
      const annotation = `\n\n[Attached media]\n${this.mediaUrls.join("\n")}`;
      let lastTextIdx = -1;
      for (let i = finalParts.length - 1; i >= 0; i -= 1) {
        if (isTextPart(finalParts[i])) {
          lastTextIdx = i;
          break;
        }
      }
      if (lastTextIdx >= 0) {
        const textPart = finalParts[lastTextIdx] as TextPart;
        finalParts[lastTextIdx] = { ...textPart, text: textPart.text + annotation };
      } else {
        finalParts.push({ type: "text", text: annotation });
      }
    }
    const parts = (finalParts.length > 0
      ? finalParts
      : [{ type: "text", text: "" }]) as unknown as UIMessage["parts"];
    const isUpdate = this.messageAdded;
    this.commitMessage(
      {
        id: this.messageId,
        role: "assistant",
        parts,
      },
      isUpdate,
    );
    this.messageAdded = true;
  }
}

export function createNormalizedChatMessageAccumulator(
  options: NormalizedChatAccumulatorOptions,
): NormalizedChatMessageAccumulator {
  return new NormalizedChatMessageAccumulator(options);
}

function parseSseDataLine(line: string): NormalizedChatStreamEvent | null {
  const normalized = line.endsWith("\r") ? line.slice(0, -1) : line;
  if (!normalized.startsWith("data:")) return null;
  const payload = normalized.slice(5).trim();
  if (!payload) return null;
  try {
    return JSON.parse(payload) as NormalizedChatStreamEvent;
  } catch {
    return null;
  }
}

export class NormalizedChatStreamParser {
  private buffer = "";

  pushChunk(chunkText: string): NormalizedChatStreamEvent[] {
    this.buffer += chunkText;
    const lines = this.buffer.split("\n");
    this.buffer = lines.pop() ?? "";
    const events: NormalizedChatStreamEvent[] = [];
    for (const line of lines) {
      const event = parseSseDataLine(line);
      if (event) events.push(event);
    }
    return events;
  }

  reset(): void {
    this.buffer = "";
  }
}

export function createNormalizedChatStreamParser(): NormalizedChatStreamParser {
  return new NormalizedChatStreamParser();
}
